/**
 * Utility functions for PostHog integration
 */

import { DisplaySurveyType } from 'posthog-js'

import type { OrganizationStatus, PartnerRole } from '@orus.eu/right-access-management'

import { getPostHogInstance } from './posthog-integration.ts'

/**
 * Gets the current PostHog session ID if available
 *
 * Uses two methods to ensure we capture the session ID:
 * 1. Direct call to get_session_id() - works when PostHog SDK is fully initialized
 * 2. Fallback to get_property('$session_id') - works even before SDK is "ready"
 *
 * The fallback is necessary because get_session_id() returns undefined when the SDK
 * is still initializing (which happens on early page loads like ensureOrusSession).
 *
 * IMPORTANT: Use '$session_id' with the $ prefix - this is PostHog's internal naming
 *
 * @returns The PostHog session ID or undefined if not available
 */
export function getPostHogSessionId(): string | undefined {
  try {
    const posthog = getPostHogInstance()
    if (!posthog) return undefined

    // Try direct call first (works when SDK is fully ready)
    const sessionId = posthog.get_session_id()
    if (sessionId) return sessionId

    // Fallback: use PUBLIC API to read from persistence
    // This works even if SDK is not fully "ready" yet
    // Note: '$session_id' with the dollar sign is the correct property name
    if (typeof posthog.get_property === 'function') {
      const persistedSessionId = posthog.get_property('$session_id')
      if (typeof persistedSessionId === 'string') {
        return persistedSessionId
      }
    }
  } catch (_error) {
    // Silently handle errors - PostHog might not be loaded yet
  }
  return undefined
}

/**
 * Gets the current PostHog distinct ID if available
 * @returns The PostHog distinct ID or undefined if not available
 */
export function getPostHogDistinctId(): string | undefined {
  try {
    const posthog = getPostHogInstance()
    if (posthog) {
      return posthog.get_distinct_id()
    }
  } catch (_error) {
    // Silently handle errors - PostHog might not be loaded yet
  }
  return undefined
}

/**
 * Gets the current PostHog anonymous ID if available
 * This returns PostHog's $device_id which is a stable identifier that persists
 * even after user identification (unlike distinct_id which changes to email)
 * @returns The PostHog anonymous ID ($device_id) or undefined if not available
 */
export function getPostHogAnonymousId(): string | undefined {
  try {
    const posthog = getPostHogInstance()
    if (posthog && typeof posthog.get_property === 'function') {
      // Use $device_id instead of distinct_id because:
      // - distinct_id changes to email after posthog.identify()
      // - $device_id remains stable across the entire user lifecycle
      return posthog.get_property('$device_id')
    }
  } catch (_error) {
    // Silently handle errors - PostHog might not be loaded yet
  }
  return undefined
}

/**
 * Identifies a user in PostHog with their user ID and attributes
 *
 * Uses user.id (not email) as the distinct_id so that the identity is stable across email changes
 * posthog-js own $identify event with person_profiles: 'always' set handles the merge of any prior anonymous Person into Person(user.id)
 */
export function identifyUserInPostHog(
  user: {
    email: string
    firstName?: string | null
    lastName?: string | null
    id: string
    role?: PartnerRole | null
  },
  organization?:
    | { technicalName: string }
    | {
        technicalName: string
        displayName: string
        status: OrganizationStatus
        isManagedByOrus: boolean
      }
    | null,
): void {
  try {
    const posthog = getPostHogInstance()
    if (!posthog) {
      return
    }
    posthog.identify(user.id, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      userId: user.id,
      role: user.role ?? null,
      ...(organization
        ? {
            organization_technical_name: organization.technicalName,
            ...('displayName' in organization
              ? {
                  organization_display_name: organization.displayName,
                  organization_status: organization.status,
                  is_managed_by_orus: organization.isManagedByOrus,
                }
              : {}),
          }
        : {}),
    })
  } catch (_error) {
    // Silently handle errors - PostHog might not be loaded yet
  }
}

/**
 * Clears the PostHog identity for the current browser. Call on logout so the next user does not collide with the previously identified Person
 * => To avoid having "Refused to merge already identified person" on the next identify() call.
 */
export function resetPostHogIdentity(): void {
  try {
    getPostHogInstance()?.reset()
  } catch (_error) {
    // Silently handle errors - PostHog might not be loaded yet
  }
}

/**
 * Initializes PostHog event renaming for activity search events
 * This function sets up event name transformations for better tracking
 */
export function initializePostHogEventRenaming(): void {
  try {
    const posthog = getPostHogInstance()
    if (posthog) {
      // Initialize any PostHog event renaming logic here
      // This is a placeholder for future PostHog event customization
    }
  } catch (_error) {
    // Silently handle errors - PostHog might not be loaded yet
  }
}

// trigger survey manually to avoid relying on event-based triggers (which only work with posthog.capture(), not Segment events)
export function showPostHogSurvey(surveyId: string): void {
  try {
    const posthog = getPostHogInstance()
    if (!posthog) return
    posthog.getSurveys((surveys) => {
      if (surveys.some((s) => s.id === surveyId)) {
        posthog.displaySurvey(surveyId, {
          displayType: DisplaySurveyType.Popover,
          ignoreConditions: false,
          ignoreDelay: false,
        })
      }
    })
  } catch (_error) {
    // Silently handle errors - PostHog might not be loaded yet
  }
}
